import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Home, LayoutGrid, ClipboardList, Receipt, Users, Utensils, MessageCircle, Phone } from 'lucide-react';
import { useTableContext } from './useTableContext';
import { useDineGuestLogin } from './useDineGuestLogin';
import { selectLineCount, type DineRootState } from './dineSlice';

/**
 * Outer shell for the guest dine-in flow.
 *
 *  - Sticky restaurant header with logo / tenant name / table chip /
 *    Group Order button (the "T4" / "Group Order" pair in the
 *    reference screens).
 *  - Bottom nav: Home / Menu / Orders / Pay Bill. Active tab is
 *    derived from the route segment; uses sticky `bottom-0` so the
 *    cart sheet and content scroll above it.
 */
export default function DineLayout() {
  // Bootstrap a guest auth session BEFORE useTableContext fires, so
  // the menu / table-lookup endpoints (which require auth on the
  // live API) succeed. Config via VITE_DINE_GUEST_USER + _PASS env
  // vars. See G39 / §32 — proper fix is a public dine endpoint.
  useDineGuestLogin();
  const { status, table, error } = useTableContext();
  const params = useParams<{ tenantCode?: string; qrToken: string }>();
  const location = useLocation();
  // Tenant comes from the route param when present, else the subdomain
  // (bill.kristysoftware.com → "bill"). The short route /dine/t/<token>
  // has no tenantCode segment, so its nav base must omit it too.
  const tenantSeg = params.tenantCode ?? tenantFromSubdomain();
  const base = params.tenantCode
    ? `/dine/${params.tenantCode}/t/${params.qrToken}`
    : `/dine/t/${params.qrToken}`;
  void tenantSeg;
  const lineCount = useSelector(selectLineCount);
  const customer = useSelector((s: DineRootState) => s.dine.customer);

  // Support contact for the floating WhatsApp / Call buttons:
  // tenant/table phone → VITE_DINE_SUPPORT_PHONE → default 9251155932.
  const dineEnv = (import.meta as unknown as { env?: Record<string, string> }).env;
  const envPhone = dineEnv?.VITE_DINE_SUPPORT_PHONE;
  // Support FABs hidden unless explicitly enabled for the deploy.
  const showSupport = /^Y(es)?$|^true$|^1$/i.test((dineEnv?.VITE_DINE_SHOW_SUPPORT ?? '').trim());
  const supportPhone =
    (table as { contactPhone?: string; tenantPhone?: string } | undefined)?.contactPhone ||
    (table as { tenantPhone?: string } | undefined)?.tenantPhone ||
    envPhone ||
    '9251155932';

  // "Group ordering is coming soon" — a polished in-page toast instead of
  // a jarring window.alert(). Auto-dismisses after a few seconds.
  const [showSoon, setShowSoon] = useState(false);
  useEffect(() => {
    if (!showSoon) return;
    const t = window.setTimeout(() => setShowSoon(false), 3500);
    return () => window.clearTimeout(t);
  }, [showSoon]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-card">
        <p className="text-sm text-content-muted">Loading your table…</p>
      </div>
    );
  }

  if (status === 'error' || !table?.tableId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-card px-6 text-center">
        <Utensils size={36} className="text-primary" />
        <h1 className="text-lg font-semibold">Couldn’t open this table</h1>
        <p className="text-sm text-content-muted">{error ?? 'Please ask staff for a fresh QR code.'}</p>
      </div>
    );
  }

  // Active-tab detection from the trailing path segment.
  const seg = location.pathname.replace(base, '').replace(/^\//, '').split('/')[0] || 'home';

  return (
    <div className="flex min-h-screen flex-col bg-surface-app">
      {/* Sticky header — logo + tenant name + table chip + Group Order */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-surface-card px-3 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary">
          {table.tenantLogoUrl ? (
            <img src={table.tenantLogoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <Utensils className="text-primary-foreground" size={20} />
          )}
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold leading-tight text-content-primary">
            {table.tenantName ?? table.tenantCode}
          </div>
          {customer?.name && (
            <div className="text-[11px] text-content-muted">Hi, {customer.name}</div>
          )}
        </div>
        <div className="flex h-8 items-center gap-1.5 rounded-md bg-surface-app px-2.5 text-xs font-medium text-content-secondary">
          <Utensils size={13} />
          {table.tableCode ?? table.tableName ?? 'Table'}
        </div>
        {seg !== 'pay-bill' && (
          <button
            type="button"
            className="relative flex h-8 items-center gap-1 rounded-md border border-border bg-surface-card px-2 text-xs font-medium text-content-secondary hover:bg-surface-card-hover"
            title="Coming soon — share this table so friends can add items too"
            onClick={() => setShowSoon(true)}
          >
            <Users size={13} />
            Group Order
            <span className="ml-1 rounded-full bg-primary-light px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
              Soon
            </span>
          </button>
        )}
      </header>

      {/* "Coming soon" toast for Group Order */}
      {showSoon && (
        <div className="fixed inset-x-0 top-16 z-50 flex justify-center px-4">
          <div className="flex items-center gap-2 rounded-full bg-content-primary px-4 py-2 text-xs font-medium text-surface-card shadow-lg">
            <Users size={13} />
            Group ordering is coming soon — share this table so friends can add items too.
          </div>
        </div>
      )}

      {/* Page body — bottom padding leaves room for the nav + cart bar. */}
      <main className="flex-1 overflow-y-auto pb-24">
        <Outlet />
      </main>

      {/* Floating WhatsApp + Call support buttons — HIDDEN by default.
          Enable per-deploy with VITE_DINE_SHOW_SUPPORT=Y (number resolves
          from the table contact / VITE_DINE_SUPPORT_PHONE / 9251155932). */}
      {showSupport && <SupportFabs phone={supportPhone} />}

      {/* Bottom nav */}
      <nav className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-4 border-t border-border bg-surface-card text-[11px]">
        <DineNavLink to={base} end label="Home" icon={<Home size={20} />} active={seg === 'home'} />
        <DineNavLink
          to={`${base}/menu`}
          label="Menu"
          icon={<LayoutGrid size={20} />}
          active={seg === 'menu'}
          badge={lineCount}
        />
        <DineNavLink
          to={`${base}/orders`}
          label="Orders"
          icon={<ClipboardList size={20} />}
          active={seg === 'orders'}
        />
        <DineNavLink
          to={`${base}/pay-bill`}
          label="Pay Bill"
          icon={<Receipt size={20} />}
          active={seg === 'pay-bill'}
        />
      </nav>
    </div>
  );
}

function DineNavLink({
  to,
  label,
  icon,
  active,
  end,
  badge,
}: {
  to: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={`flex flex-col items-center justify-center gap-0.5 py-2 ${
        active ? 'text-primary' : 'text-content-muted'
      }`}
    >
      <div className="relative">
        {icon}
        {badge && badge > 0 ? (
          <span className="absolute -right-2 -top-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {badge}
          </span>
        ) : null}
      </div>
      <span className={active ? 'font-semibold' : ''}>{label}</span>
    </NavLink>
  );
}

/**
 * Floating WhatsApp + Call support buttons, fixed bottom-right above the
 * nav bar. WhatsApp opens wa.me/<intl>; Call opens a `tel:` link. The
 * number defaults to 9251155932 (passed in by the layout).
 */
function SupportFabs({ phone }: { phone: string }) {
  const digits = (phone || '').replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return null;
  const intl = digits.length === 10 ? `91${digits}` : digits;
  const waText = encodeURIComponent('Hi! I need some help with my table order.');
  return (
    <div className="fixed bottom-24 right-4 z-30 flex flex-col gap-3">
      <a
        href={`https://wa.me/${intl}?text=${waText}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        title="Chat on WhatsApp"
        className="grid h-12 w-12 place-items-center rounded-full bg-success text-white shadow-lg transition-transform hover:scale-105"
      >
        <MessageCircle size={22} />
      </a>
      <a
        href={`tel:+${intl}`}
        aria-label="Call us"
        title="Call us"
        className="grid h-12 w-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105"
      >
        <Phone size={20} />
      </a>
    </div>
  );
}

/**
 * Derive the tenant short-code from the hostname's first label
 * (bill.kristysoftware.com → "bill"). Returns '' for localhost / IPs /
 * bare 2-label domains where no subdomain is present.
 */
function tenantFromSubdomain(): string {
  if (typeof window === 'undefined') return '';
  const h = window.location.hostname.toLowerCase();
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(h)) return '';
  const parts = h.split('.').filter(Boolean);
  if (parts.length < 3) return '';
  const first = parts[0];
  return ['www', 'app', 'admin', 'panel', 'web', 'portal'].includes(first) ? '' : first;
}
